import {
  Avatar,
  ButtonDS,
  DialogModalDS,
  Spacer,
  TableDS,
  TextCapitalized,
  unreachable,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCallback, useMemo, useState } from "react";
import { DriversListType } from "../../drivers/driversAPI";
import {
  DriversListFormDisplayed,
  DriversListFormDisplayedRow,
} from "../electrificationApi";
import { EmptyTable, MultipleDrivers } from "@qivia/ui/src/assets/assets";
import "../../../../i18n/i18n";

export const DriversToSendFormModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
  drivers: DriversListType[];
  onSubmit: (drivers: DriversListFormDisplayedRow[]) => void;
}) => {
  const { t } = useTranslation();
  const [rowsChecked, setRowsChecked] = useState<DriversListFormDisplayedRow[]>(
    [],
  );
  const { onSubmit, onCloseModal, drivers } = props;

  const driversSorted = useMemo(() => {
    return [...drivers].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [drivers]);

  const onSubmitForm = useCallback(() => {
    setRowsChecked([]);
    onSubmit(rowsChecked);
    onCloseModal();
  }, [onCloseModal, onSubmit, rowsChecked]);

  const onCloseForm = useCallback(() => {
    setRowsChecked([]);
    onCloseModal();
  }, [onCloseModal]);

  const render =
    (row: DriversListFormDisplayedRow) =>
    (key: keyof DriversListFormDisplayed) => {
      switch (key) {
        case "name":
          return (
            <StyledRowCenter>
              <TextCapitalized>
                <Avatar
                  size={"S"}
                  color={"red"}
                  firstName={row[key].split(" ")[0][0]}
                  lastName={row[key].split(" ")[1][0]}
                />
              </TextCapitalized>
              <Spacer x={0.5} />
              {row[key]}
            </StyledRowCenter>
          );
      }
      unreachable(key);
    };

  const headers = {
    name: {
      text: "",
    },
  };

  return (
    <DialogModalDS visible={props.visible} onClose={onCloseForm} width={60}>
      <StyledUserRoleModalContainer>
        <Spacer y={1} />
        <StyledTitle>
          <TextCapitalized>
            {t(`users.drivers.modal.form.title`)}
          </TextCapitalized>
        </StyledTitle>
        <Spacer y={1.5} />
        <StyledSubTitle>
          <TextCapitalized>
            {t(`users.drivers.modal.form.subTitle`)}
          </TextCapitalized>
        </StyledSubTitle>
        <Spacer y={2} />
        <StyledTableContainer>
          <TableDS<keyof DriversListFormDisplayed, DriversListFormDisplayed>
            data={driversSorted}
            headers={headers}
            render={render}
            rowsChecked={rowsChecked}
            setRowsChecked={setRowsChecked}
            width={30}
            height={25}
            headerOverlayed={
              <>
                <MultipleDrivers />
                <Spacer x={0.5} />
                {t(`users.drivers.modal.form.header`, {
                  numberOfDrivers: rowsChecked.length,
                })}
              </>
            }
            emptyContent={
              <StyledFlex>
                <Spacer x={1.5} />
                <StyledEmptyTable>
                  <Spacer y={2} />
                  <EmptyTable />
                  <Spacer y={1.5} />
                  <StyledTitleEmptyTable>
                    <TextCapitalized>
                      {t("users.drivers.modal.form.empty.title")}
                    </TextCapitalized>
                  </StyledTitleEmptyTable>
                  <Spacer y={1} />
                  <StyledSubText>
                    <TextCapitalized>
                      {t("users.drivers.modal.form.empty.body")}
                    </TextCapitalized>
                  </StyledSubText>
                  <Spacer y={2} />
                </StyledEmptyTable>
                <Spacer x={1.5} />
              </StyledFlex>
            }
          />
        </StyledTableContainer>
        <Spacer y={2} />
        <div>
          <ButtonDS
            text={t("electrification.header.title.survey")}
            format={"fill"}
            buttonType={"primary"}
            sizeButton={"S"}
            onClick={onSubmitForm}
          />
        </div>
        <Spacer y={3} />
      </StyledUserRoleModalContainer>
    </DialogModalDS>
  );
};

const StyledTableContainer = styled.div`
  max-height: 25rem;
`;

const StyledTitleEmptyTable = styled.div`
  display: flex;
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/black"]};
`;

const StyledSubText = styled.div`
  display: flex;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledUserRoleModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  height: 100%;
`;

const StyledTitle = styled.div`
  ${typographies["Header/H1"]}
`;

const StyledSubTitle = styled.div`
  color: ${colors["colors/text/darkGrey"]};
  ${typographies["Body/XS"]}
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
